import type { UserRole } from 'fuel-benchmark-contracts';
import { Car, FileText, Gauge, GitCompare, ListChecks, LogOut, Route as RouteIcon, Upload, Users, type LucideIcon } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  roles?: readonly UserRole[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: Gauge },
  { to: '/vehicles', labelKey: 'nav.vehicles', icon: Car },
  { to: '/conditions', labelKey: 'nav.conditions', icon: ListChecks },
  { to: '/runs', labelKey: 'nav.runs', icon: RouteIcon },
  { to: '/import', labelKey: 'nav.import', icon: Upload },
  { to: '/compare', labelKey: 'nav.compare', icon: GitCompare },
  { to: '/reports', labelKey: 'nav.reports', icon: FileText },
  { to: '/settings/users', labelKey: 'nav.users', icon: Users, roles: ['MANAGER'] },
];

export const AppShell = () => {
  const { t } = useI18nTranslation();
  const { user, logout } = useAuth();

  const visibleItems = NAV_ITEMS.filter((item) => !item.roles || (user && item.roles.includes(user.role)));

  return (
    <div className="flex h-full min-h-screen bg-background">
      <aside className="flex w-64 shrink-0 flex-col border-e border-border bg-card">
        <div className="border-b border-border px-5 py-6">
          <p className="text-base font-semibold tracking-tight text-card-foreground">{t('app.name')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('app.tagline')}</p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {visibleItems.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              <Icon className="size-4 shrink-0" />
              <span className="truncate">{t(labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          {user ? (
            <div className="px-2 pb-2">
              <p className="truncate text-sm font-medium text-card-foreground">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t(`roles.${user.role}`)}</p>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={logout}
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogOut className="size-3.5" />
              {t('actions.signOut')}
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto p-6">
        <Outlet />
      </main>
    </div>
  );
};
